/**
 * WAF CDN platforms (Owl/Mlytics — DomainsService / PlatformsService) — per-domain
 * CDN platform management. Service-scoped and domain-anchored (`?domain=<fqdn>`);
 * the provider resolves the customer (tier-2) from the zone.
 *
 * Backed by `modules/service/waf/controller_platforms.go`:
 *   - GET   /services/:service_id/waf/platforms/domain                   → a domain's CDN platforms (paginated)
 *   - PATCH /services/:service_id/waf/platforms/domain/:platform/default → Mlytics-managed (default) mode + enable
 *   - PATCH /services/:service_id/waf/platforms/domain/:platform/byo     → BYO mode + custom edge domain
 *
 * The backend exposes only these two mode-switch PATCHes — there is no
 * attach/detach of arbitrary platforms and no weight/priority config. `:platform`
 * is the platform name/slug (e.g. "vncdn"). The list nests the array under
 * `data.platforms`, so it's unwrapped as an object (not the flat list envelope).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import {
  serviceApiV3,
  wafKeys,
  unwrap,
  type DomainPlatform,
  type ListResult,
  type PageInfo,
} from './_shared';

/** List a domain's CDN platforms (paginated). */
export function useWafDomainPlatforms(
  serviceId: string | undefined,
  domain: string | undefined,
  filter: { page?: number; page_size?: number } = {},
) {
  const params = { domain, ...filter };
  return useQuery<ListResult<DomainPlatform>>({
    queryKey: wafKeys.domainPlatforms(serviceId ?? '', domain ?? '', filter as Record<string, unknown>),
    enabled: !!serviceId && !!domain,
    staleTime: 15_000,
    queryFn: async () => {
      const r = unwrap<{ platforms?: DomainPlatform[]; pagination?: PageInfo }>(
        await serviceApiV3.get(`/services/${serviceId}/waf/platforms/domain`, { params }),
      );
      return { items: r?.platforms ?? [], pagination: r?.pagination };
    },
  });
}

/**
 * Switch a platform to Mlytics-managed (default) mode and toggle its enable flag
 * (`PATCH /waf/platforms/domain/:platform/default`). This is also how a platform
 * is turned on/off.
 */
export function useSetPlatformDefault(
  serviceId: string | undefined,
  options?: UseMutationOptions<DomainPlatform, Error, { domain: string; platform: string; enable: boolean }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, platform, enable }: { domain: string; platform: string; enable: boolean }) =>
      unwrap<DomainPlatform>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/platforms/domain/${platform}/default`, { enable }, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      const vars = args[1] as { domain: string };
      if (serviceId) {
        qc.invalidateQueries({ queryKey: ['waf', serviceId, 'domain-platforms'] });
        qc.invalidateQueries({ queryKey: wafKeys.domain(serviceId, vars.domain) });
      }
      options?.onSuccess?.(...args);
    },
  });
}

/**
 * Switch a platform to BYO mode with a custom edge domain
 * (`PATCH /waf/platforms/domain/:platform/byo`).
 */
export function useSetPlatformBYO(
  serviceId: string | undefined,
  options?: UseMutationOptions<DomainPlatform, Error, { domain: string; platform: string; edge_domain: string }>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ domain, platform, edge_domain }: { domain: string; platform: string; edge_domain: string }) =>
      unwrap<DomainPlatform>(
        await serviceApiV3.patch(`/services/${serviceId}/waf/platforms/domain/${platform}/byo`, { edge_domain }, {
          params: { domain },
        }),
      ),
    ...options,
    onSuccess: (...args) => {
      const vars = args[1] as { domain: string };
      if (serviceId) {
        qc.invalidateQueries({ queryKey: ['waf', serviceId, 'domain-platforms'] });
        qc.invalidateQueries({ queryKey: wafKeys.domain(serviceId, vars.domain) });
      }
      options?.onSuccess?.(...args);
    },
  });
}
